import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            setUser(null);
            setLoading(false);
            return null;
        }
        // Retry up to 2 times on transient failures
        for (let attempt = 0; attempt < 3; attempt++) {
            try {
                const res = await api.get("/auth/me");
                setUser(res.data);
                setLoading(false);
                return res.data;
            } catch (e) {
                const status = e?.response?.status;
                if (status === 401 || status === 403) {
                    localStorage.removeItem("token");
                    setUser(null);
                    setLoading(false);
                    return null;
                }
                // transient — wait and retry
                if (attempt < 2) {
                    await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
                    continue;
                }
            }
        }
        setLoading(false);
        return null;
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = async (email, password) => {
        const res = await api.post("/auth/login", { email, password });
        localStorage.setItem("token", res.data.access_token);
        await refresh();
        return res.data;
    };
    const signup = async (email, password, display_name) => {
        const res = await api.post("/auth/signup", { email, password, display_name });
        localStorage.setItem("token", res.data.access_token);
        await refresh();
        return res.data;
    };
    const logout = () => {
        localStorage.removeItem("token");
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, signup, logout, refresh }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
